import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, X, Search } from "lucide-react";
import Checkbox from "../Checkbox/Checkbox";
import styles from "./MultiSelect.module.css";

/**
 * MultiSelect Component
 *
 * A multi-select dropdown with checkbox options and search functionality
 *
 * @param {string} label - Optional label above the select
 * @param {array} options - Array of string options
 * @param {array} selected - Array of selected values (or use 'value' prop)
 * @param {array} value - Array of selected values (alias for 'selected')
 * @param {function} onChange - Callback when selection changes
 * @param {string} placeholder - Placeholder text
 * @param {string} size - Size variant: 'sm' | 'md' (default) | 'lg' | 'compact' (for tables)
 */
const MultiSelect = ({
  label,
  options = [],
  selected,
  value,
  onChange,
  placeholder = "Select...",
  size = "md",
}) => {
  // Support both 'selected' and 'value' prop names for compatibility
  const selectedValues = selected !== undefined ? selected : value || [];
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState("below");
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm(""); // Clear search when closing
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const trigger = dropdownRef.current;
      const triggerRect = trigger.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;
      const menuHeight = 350;

      if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
        setDropdownPosition("above");
      } else {
        setDropdownPosition("below");
      }
    }
  }, [isOpen]);

  const handleToggle = (option) => {
    if (selectedValues.includes(option)) {
      onChange(selectedValues.filter((item) => item !== option));
    } else {
      onChange([...selectedValues, option]);
    }
  };

  const handleSelectAll = () => {
    const currentOptions = options || [];
    if (selectedValues.length === currentOptions.length) {
      onChange([]);
    } else {
      onChange(currentOptions);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const removeTag = (option, e) => {
    e.stopPropagation();
    onChange(selectedValues.filter((item) => item !== option));
  };

  // Filter options based on search term
  const filteredOptions = options.filter((option) =>
    option.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className={styles.container} ref={dropdownRef}>
      {label && <label className={styles.label}>{label}</label>}
      <div
        className={`${styles.trigger} ${styles[size]} ${isOpen ? styles.open : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className={styles.tagsContainer}>
          {selectedValues.length === 0 ? (
            <span className={styles.placeholder}>{placeholder}</span>
          ) : (
            selectedValues.map((val) => (
              <span key={val} className={styles.tag}>
                <span className={styles.tagText}>{val}</span>
                <button
                  className={styles.tagRemove}
                  onClick={(e) => removeTag(val, e)}
                  title={`Remove ${val}`}
                >
                  <X size={12} />
                </button>
              </span>
            ))
          )}
        </div>
        <div className={styles.actions}>
          {selectedValues.length > 0 && (
            <button
              className={styles.clearButton}
              onClick={handleClear}
              title="Clear all"
            >
              <X size={size === "compact" ? 12 : 14} />
            </button>
          )}
          <ChevronDown
            size={size === "compact" ? 14 : 16}
            className={`${styles.arrow} ${isOpen ? styles.arrowOpen : ""}`}
          />
        </div>
      </div>

      {isOpen && (
        <div
          ref={menuRef}
          className={`${styles.menu} ${
            dropdownPosition === "above" ? styles.menuAbove : ""
          }`}
        >
          {/* Search Input */}
          <div className={styles.searchContainer}>
            <Search size={14} className={styles.searchIcon} />
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className={styles.searchInput}
              onClick={(e) => e.stopPropagation()}
            />
            {searchTerm && (
              <button
                className={styles.searchClear}
                onClick={(e) => {
                  e.stopPropagation();
                  setSearchTerm("");
                  searchInputRef.current?.focus();
                }}
              >
                <X size={14} />
              </button>
            )}
          </div>

          <div className={styles.menuHeader}>
            <button
              className={styles.selectAllButton}
              onClick={handleSelectAll}
            >
              {selectedValues.length === options.length
                ? "Deselect All"
                : "Select All"}
            </button>
          </div>
          <div className={styles.options}>
            {filteredOptions.length === 0 ? (
              <div className={styles.noResults}>
                No results found for "{searchTerm}"
              </div>
            ) : (
              filteredOptions.map((option) => (
                <div key={option} className={styles.option}>
                  <Checkbox
                    label={option}
                    checked={selectedValues.includes(option)}
                    onChange={() => handleToggle(option)}
                  />
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
